import type { ReactNode } from "react";
import type { GrahaId, ProvenanceKind } from "./types";

const GRAHA_LABELS: Record<string, { short: string; name: string }> = {
  sun: { short: "Su", name: "Surya" },
  moon: { short: "Mo", name: "Chandra" },
  mars: { short: "Ma", name: "Mangal" },
  mercury: { short: "Bu", name: "Budh" },
  jupiter: { short: "Ju", name: "Jupiter" },
  venus: { short: "Shu", name: "Shukra" },
  saturn: { short: "Sha", name: "Shani" },
  rahu: { short: "Ra", name: "Rahu" },
  ketu: { short: "Ke", name: "Ketu" },
};

const PROVENANCE_LABELS: Record<ProvenanceKind, string> = {
  engine: "Kundali engine",
  rule: "Rule anchored",
  interpretation: "Personal interpretation",
  pending: "Reference review pending",
};

function cx(...classes: Array<string | undefined | false>): string {
  return classes.filter(Boolean).join(" ");
}

export function GrahaGlyph({
  graha,
  size = "md",
  className,
}: {
  graha: GrahaId;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const key = String(graha).toLowerCase();
  const grahaLabel = GRAHA_LABELS[key] ?? {
    short: key.slice(0, 2).toUpperCase(),
    name: String(graha),
  };
  return (
    <span
      className={cx("jyotish-graha", `jyotish-graha--${size}`, className)}
      role="img"
      aria-label={grahaLabel.name}
      title={grahaLabel.name}
    >
      {grahaLabel.short}
    </span>
  );
}

export function BhavaBadge({ house, label }: { house: number; label?: string }) {
  return (
    <span className="jyotish-bhava">
      <span className="jyotish-bhava__number">{house}</span>
      <span>{label ?? `${house}${ordinalSuffix(house)} house`}</span>
    </span>
  );
}

export function ProvenanceBadge({
  kind,
  label,
  className,
}: {
  kind: ProvenanceKind;
  label?: string;
  className?: string;
}) {
  return (
    <span className={cx("jyotish-provenance", `jyotish-provenance--${kind}`, className)}>
      <span className="jyotish-provenance__mark" aria-hidden />
      {label ?? PROVENANCE_LABELS[kind]}
    </span>
  );
}

export function JyotishScore({
  value,
  max,
  label,
  note,
}: {
  value: number;
  max: number;
  label: string;
  note?: string;
}) {
  const safeMax = Math.max(max, 1);
  const safeValue = Math.min(Math.max(value, 0), safeMax);
  return (
    <section className="jyotish-score" aria-label={`${label}: ${safeValue} out of ${safeMax}`}>
      <div className="jyotish-score__header">
        <span>{label}</span>
        <strong>
          {safeValue}/{safeMax}
        </strong>
      </div>
      <meter min={0} max={safeMax} value={safeValue} className="jyotish-score__meter">
        {safeValue} out of {safeMax}
      </meter>
      {note ? <p>{note}</p> : null}
    </section>
  );
}

export function UpayCard({
  title,
  rationale,
  children,
  provenance = "rule",
}: {
  title: string;
  rationale?: string;
  children: ReactNode;
  provenance?: ProvenanceKind;
}) {
  return (
    <article className="jyotish-upay">
      <div className="jyotish-upay__header">
        <div>
          <span className="jyotish-upay__eyebrow">Upay</span>
          <h3>{title}</h3>
        </div>
        <ProvenanceBadge kind={provenance} />
      </div>
      {rationale ? <p className="jyotish-upay__rationale">{rationale}</p> : null}
      <div className="jyotish-upay__body">{children}</div>
    </article>
  );
}

function ordinalSuffix(value: number): string {
  const mod100 = value % 100;
  if (mod100 >= 11 && mod100 <= 13) return "th";
  if (value % 10 === 1) return "st";
  if (value % 10 === 2) return "nd";
  if (value % 10 === 3) return "rd";
  return "th";
}
